import { useEffect, useRef, useState } from "react"
import { Input } from "components/ui/inputs/Input"
import { CreateAreaSidebar } from "components/ui/customWebviewer/shared/CreateAreaSidebar/CreateAreaSidebar"
import style from "./CreateErefSidebar.module.scss"

const { fields, inputBlock, label, input } = style

// The backend caps `elevation_number` at 24 characters; stop the value at the source rather than
// letting the user type a string that can only come back as a 400.
const MAX_ELEVATION_NUMBER_LENGTH = 24

type Props = {
	/**
	 * The area drawn on the drawing, or null while the user still has to draw one — which is the
	 * difference between the panel's two states: the instruction, or the fields + footer.
	 */
	area: { pageNumber: number } | null
	/** Name of the page the area was drawn on; null when that page has no name yet. */
	pageName: string | null
	/** Pre-filled elevation number (the next free one on that page). */
	suggestedElevationNumber: string
	isPending?: boolean
	onCancel: () => void
	onCreate: (elevationNumber: string) => void
	onClose: () => void
}

/**
 * Right-panel companion of the "add elevation reference" mode (REQ-292). It has exactly two states:
 * before an area is drawn it only explains the gesture, and once one exists it asks for the
 * elevation number and offers Cancel / Create elevation.
 *
 * `Page` is read-only by design: it is whatever page the area was drawn on, not a choice. A page
 * with no name can't be submitted at all (the endpoint requires `page_name`), so Create is blocked
 * with an explanation rather than failing on the server.
 */
export const CreateErefSidebar = ({
	area,
	pageName,
	suggestedElevationNumber,
	isPending = false,
	onCancel,
	onCreate,
	onClose,
}: Props) => {
	const [elevationNumber, setElevationNumber] = useState(suggestedElevationNumber)
	// Re-drawing the area on a different page changes what the next free number is; adopt the new
	// suggestion, since anything the user typed was for the page they just left.
	useEffect(() => {
		setElevationNumber(suggestedElevationNumber)
	}, [suggestedElevationNumber, area?.pageNumber])

	const numberFieldRef = useRef<HTMLDivElement>(null)
	const trimmedNumber = elevationNumber.trim()
	const canCreate = !!area && !!pageName && trimmedNumber.length > 0 && !isPending

	return (
		<CreateAreaSidebar
			title="Elevation reference"
			hint="Drag on the drawing to define the elevation area"
			area={area}
			isPending={isPending}
			canSubmit={canCreate}
			submitLabel="Create elevation"
			onCancel={onCancel}
			onClose={onClose}
			onSubmit={() => onCreate(trimmedNumber)}
			focusTargetRef={numberFieldRef}
			selectFocusTargetText
			error={pageName ? undefined : "This page has no name yet. Name it first to add an elevation reference here."}
		>
			<div className={fields}>
				<div className={inputBlock} ref={numberFieldRef}>
					<span className={label}>Elevation number</span>
					<Input
						className={input}
						value={elevationNumber}
						onChange={(e) => setElevationNumber(e.target.value.slice(0, MAX_ELEVATION_NUMBER_LENGTH))}
					/>
				</div>
				<div className={inputBlock}>
					<span className={label}>Page</span>
					<Input className={input} value={pageName ?? ""} disabled />
				</div>
			</div>
		</CreateAreaSidebar>
	)
}
